import React, { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  TextField,
  Paper,
  Divider,
  InputAdornment,
} from "@mui/material";
import { TDS_RATES } from "./tdsRates";
import {
  numberToWords,
  formatIndianCurrency,
  parseCurrency,
} from "../../../utils/numberToWords";

const TDSCalculator = () => {
  const [recipientType, setRecipientType] = useState(
    "Individual/HUF/Sole Proprietor"
  );
  const [panAvailable, setPanAvailable] = useState(true);
  const [natureCode, setNatureCode] = useState("");
  const [amount, setAmount] = useState(0);
  const [amountDisplay, setAmountDisplay] = useState("0");

  const selectedPayment = TDS_RATES[natureCode];
  const amountNum = Number(amount) || 0;

  let rate = 0;
  let applicable = true;

  if (selectedPayment) {
    applicable = selectedPayment.applicableTo.includes(recipientType);
    rate = applicable
      ? panAvailable
        ? selectedPayment.baseRate
        : selectedPayment.noPanRate
      : 0;
  }

  const tdsAmount = applicable && amountNum > 0 ? (amountNum * rate) / 100 : 0;

  const handleAmountChange = (val) => {
    const num = parseCurrency(val);
    setAmount(num);
    setAmountDisplay(formatIndianCurrency(num));
  };

  return (
    <Box>
      <Grid container spacing={6}>
        <Grid item xs={12} md={7}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {/* Recipient Type */}
            <FormControl component="fieldset">
              <FormLabel
                sx={{
                  fontWeight: 700,
                  color: "#333",
                  mb: 1,
                  fontSize: "0.9rem",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                Recipient Category
              </FormLabel>
              <RadioGroup
                row
                value={recipientType}
                onChange={(e) => setRecipientType(e.target.value)}
              >
                <FormControlLabel
                  value="Individual/HUF/Sole Proprietor"
                  control={
                    <Radio
                      sx={{
                        "&.Mui-checked": { color: "var(--primary-color)" },
                      }}
                    />
                  }
                  label="Individual / HUF / Sole Proprietor"
                />
                <FormControlLabel
                  value="Others"
                  control={
                    <Radio
                      sx={{
                        "&.Mui-checked": { color: "var(--primary-color)" },
                      }}
                    />
                  }
                  label="Others (Companies/Firms)"
                />
              </RadioGroup>
            </FormControl>

            <Divider />

            {/* PAN Availability */}
            <FormControl component="fieldset">
              <FormLabel
                sx={{
                  fontWeight: 700,
                  color: "#333",
                  mb: 1,
                  fontSize: "0.9rem",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                Is Recipient's PAN Available?
              </FormLabel>
              <RadioGroup
                row
                value={panAvailable ? "yes" : "no"}
                onChange={(e) => setPanAvailable(e.target.value === "yes")}
              >
                <FormControlLabel
                  value="yes"
                  control={
                    <Radio
                      sx={{
                        "&.Mui-checked": { color: "var(--primary-color)" },
                      }}
                    />
                  }
                  label="Yes (Lower Rate)"
                />
                <FormControlLabel
                  value="no"
                  control={
                    <Radio
                      sx={{
                        "&.Mui-checked": { color: "var(--primary-color)" },
                      }}
                    />
                  }
                  label="No (Higher Rate)"
                />
              </RadioGroup>
            </FormControl>

            <Divider />

            {/* Nature of Payment */}
            <Box>
              <FormLabel
                sx={{
                  fontWeight: 700,
                  color: "#333",
                  mb: 2,
                  display: "block",
                  fontSize: "0.9rem",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                Nature of Payment
              </FormLabel>
              <FormControl fullWidth>
                <Select
                  value={natureCode}
                  onChange={(e) => setNatureCode(e.target.value)}
                  displayEmpty
                  sx={{ borderRadius: "12px", bgcolor: "#fcfcfc" }}
                >
                  <MenuItem value="">
                    <Typography sx={{ color: "#aaa" }}>
                      Select Transaction Type
                    </Typography>
                  </MenuItem>
                  {Object.keys(TDS_RATES).map((code) => (
                    <MenuItem key={code} value={code}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                          gap: 2,
                        }}
                      >
                        <Typography sx={{ fontWeight: 600 }}>
                          Section {code}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#666",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {TDS_RATES[code].name}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Amount */}
            <Box>
              <FormLabel
                sx={{
                  fontWeight: 700,
                  color: "#333",
                  mb: 2,
                  display: "block",
                  fontSize: "0.9rem",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                Gross Amount to be Paid
              </FormLabel>
              <TextField
                fullWidth
                value={amountDisplay}
                onChange={(e) => handleAmountChange(e.target.value)}
                placeholder="0"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                  sx: { borderRadius: "12px", bgcolor: "#fcfcfc" },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "var(--primary-color)",
                  fontWeight: 600,
                  mt: 1,
                  display: "block",
                }}
              >
                {numberToWords(amount)}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              height: "100%",
              minHeight: "300px",
              borderRadius: "24px",
              bgcolor: "var(--primary-color)",
              color: "#fff",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              boxShadow: "0 20px 40px rgba(26, 66, 161, 0.2)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Background design element */}
            <Box
              sx={{
                position: "absolute",
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.05)",
                zIndex: 0,
              }}
            />

            <Box sx={{ position: "relative", zIndex: 1, width: "100%" }}>
              <Typography
                variant="overline"
                sx={{ opacity: 0.8, letterSpacing: 2 }}
              >
                Estimated Tax Deduction
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  color: "var(--tertiary-color)",
                  fontWeight: 800,
                  my: 2,
                  fontFamily: "'Roboto Slab', serif",
                  fontSize: { xs: "2.5rem", md: "3rem" },
                }}
              >
                ₹
                {tdsAmount.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "var(--tertiary-color)",
                  opacity: 0.9,
                  fontWeight: 600,
                  display: "block",
                  mb: 2,
                }}
              >
                {numberToWords(tdsAmount)}
              </Typography>

              <Box
                sx={{
                  mt: 4,
                  pt: 4,
                  borderTop: "1px solid rgba(255,255,255,0.1)",
                  textAlign: "left",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.7,
                        display: "block",
                        color: "var(--tertiary-color)",
                      }}
                    >
                      Applied Rate
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 700, color: "var(--tertiary-color)" }}
                    >
                      {applicable ? `${rate}%` : "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.7,
                        display: "block",
                        color: "var(--tertiary-color)",
                      }}
                    >
                      Net Payable
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 700, color: "var(--tertiary-color)" }}
                    >
                      ₹
                      {(amountNum - tdsAmount).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {!applicable && (
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    bgcolor: "rgba(255,0,0,0.1)",
                    borderRadius: "12px",
                    border: "1px solid rgba(255,255,255,0.2)",
                  }}
                >
                  <Typography variant="body2">
                    Note: This section is not applicable to the selected
                    recipient type.
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TDSCalculator;
